import { useRef, useEffect, KeyboardEvent, ClipboardEvent, ChangeEvent, FocusEvent } from 'react';

export interface OtpInputProps {
	/** Number of digits (default: 6) */
	length?: number;
	/** Current OTP value */
	value: string;
	/** Callback when OTP changes */
	onChange: (otp: string) => void;
	/** Callback when all digits are entered */
	onComplete?: (otp: string) => void;
	/** Whether the input is disabled */
	disabled?: boolean;
	/** Auto focus the first input on mount */
	autoFocus?: boolean;
	/** Error state */
	error?: boolean;
}

const OtpInput = ({
	length = 6,
	value,
	onChange,
	onComplete,
	disabled = false,
	autoFocus = true,
	error = false,
}: OtpInputProps) => {
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Convert value string to array of digits
	const otpArray = value.split('').slice(0, length);
	while (otpArray.length < length) {
		otpArray.push('');
	}

	// Auto focus first input on mount
	useEffect(() => {
		if (autoFocus && inputRefs.current[0]) {
			inputRefs.current[0].focus();
		}
	}, [autoFocus]);

	// Call onComplete when all digits are entered
	useEffect(() => {
		if (value.length === length && onComplete) {
			onComplete(value);
		}
	}, [value, length, onComplete]);

	const focusInput = (index: number) => {
		if (index >= 0 && index < length && inputRefs.current[index]) {
			inputRefs.current[index]?.focus();
		}
	};

	const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;

		// Only allow digits
		if (inputValue && !/^\d$/.test(inputValue)) {
			return;
		}

		// Update the OTP value
		const newOtpArray = [...otpArray];
		newOtpArray[index] = inputValue;
		const newOtp = newOtpArray.join('');
		onChange(newOtp);

		// Move to next input if a digit was entered
		if (inputValue && index < length - 1) {
			focusInput(index + 1);
		}
	};

	const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
		// Handle backspace
		if (e.key === 'Backspace') {
			e.preventDefault();
			const newOtpArray = [...otpArray];

			if (otpArray[index]) {
				// Clear current input
				newOtpArray[index] = '';
				onChange(newOtpArray.join(''));
			} else if (index > 0) {
				// Move to previous input and clear it
				newOtpArray[index - 1] = '';
				onChange(newOtpArray.join(''));
				focusInput(index - 1);
			}
		}

		// Handle left arrow
		if (e.key === 'ArrowLeft' && index > 0) {
			e.preventDefault();
			focusInput(index - 1);
		}

		// Handle right arrow
		if (e.key === 'ArrowRight' && index < length - 1) {
			e.preventDefault();
			focusInput(index + 1);
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

		if (pastedData) {
			onChange(pastedData);
			// Focus the input after the last pasted digit, or the last input
			focusInput(Math.min(pastedData.length, length - 1));
		}
	};

	const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
		// Select the input content on focus
		e.target.select();
	};

	return (
		<div className="otp-input">
			{otpArray.map((digit, index) => (
				<input
					key={index}
					ref={el => {
						inputRefs.current[index] = el;
					}}
					type="text"
					inputMode="numeric"
					maxLength={1}
					value={digit}
					onChange={e => handleChange(index, e)}
					onKeyDown={e => handleKeyDown(index, e)}
					onPaste={handlePaste}
					onFocus={handleFocus}
					disabled={disabled}
					className={`otp-input__field ${error ? 'otp-input__field--error' : ''} ${
						digit ? 'otp-input__field--filled' : ''
					}`}
					autoComplete="one-time-code"
					aria-label={`Digit ${index + 1} of ${length}`}
				/>
			))}
		</div>
	);
};

export default OtpInput;
